/* ergonix-stories.js
   Instagram-style fullscreen story video viewer.
   Vanilla JS (no import-map / Component dependency) so it cannot break the way
   the quiz bundle did. Loaded as a plain deferred script.

   BUNDLE: ships together with sections/ergonix-stories.liquid + assets/ergonix-stories.css
   (see SENSITIVE-CONTRACTS.md - do not push one without the others).

   Each `[data-ergonix-stories]` root owns its own fullscreen viewer (appended to
   <body> to escape sticky-header stacking contexts). Stories are read from the
   tile buttons' data-* attributes. */

(function () {
  'use strict';

  var SWIPE_CLOSE_PX = 80;

  function ErgonixStories(root) {
    this.root = root;
    this.tiles = Array.prototype.slice.call(root.querySelectorAll('.erx-stories__tile'));
    this.stories = this.tiles.map(function (tile) {
      return {
        video: tile.getAttribute('data-video') || '',
        poster: tile.getAttribute('data-poster') || '',
        title: tile.getAttribute('data-title') || ''
      };
    }).filter(function (s) { return s.video; });

    if (!this.stories.length) return;

    this.index = 0;
    this.viewer = null;
    this.raf = 0;
    this.muted = false;
    this.userPaused = false;
    this.touchStartY = null;
    this.lastFocus = null;

    var self = this;
    this.tiles.forEach(function (tile, i) {
      tile.addEventListener('click', function () { self.open(i); });
    });

    // Desktop scroll arrows (prev + next)
    this.viewport = root.querySelector('.erx-stories__viewport');
    this.row = root.querySelector('.erx-stories__row');
    this.nextBtn = root.querySelector('.erx-stories__scroll--next');
    this.prevBtn = root.querySelector('.erx-stories__scroll--prev');
    if (this.row && (this.nextBtn || this.prevBtn)) {
      var update = function () { self.updateArrow(); };
      var step = function () { return Math.round(self.row.clientWidth * 0.85); };
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () {
        self.row.scrollBy({ left: step(), behavior: 'smooth' });
      });
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () {
        self.row.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      this.row.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(update);
      else update();
    }
  }

  ErgonixStories.prototype.updateArrow = function () {
    if (!this.row) return;
    var maxScroll = this.row.scrollWidth - this.row.clientWidth;
    var overflow = maxScroll > 4;
    var atEnd = this.row.scrollLeft >= maxScroll - 4;
    var atStart = this.row.scrollLeft <= 4;
    if (this.nextBtn) this.nextBtn.hidden = !overflow || atEnd;
    if (this.prevBtn) this.prevBtn.hidden = !overflow || atStart;
    if (this.viewport) {
      this.viewport.classList.toggle('has-overflow', overflow && !atEnd);
      this.viewport.classList.toggle('has-overflow-start', overflow && !atStart);
    }
  };

  ErgonixStories.prototype.buildViewer = function () {
    if (this.viewer) return;
    var self = this;

    var v = document.createElement('div');
    v.className = 'erx-stories-viewer';
    v.setAttribute('role', 'dialog');
    v.setAttribute('aria-modal', 'true');
    v.hidden = true;

    var bars = this.stories.map(function () {
      return '<div class="erx-stories-viewer__bar"><div class="erx-stories-viewer__bar-fill"></div></div>';
    }).join('');

    v.innerHTML =
      '<div class="erx-stories-viewer__stage">' +
        '<video class="erx-stories-viewer__video" playsinline webkit-playsinline></video>' +
        '<div class="erx-stories-viewer__scrim erx-stories-viewer__scrim--top"></div>' +
        '<div class="erx-stories-viewer__scrim erx-stories-viewer__scrim--bottom"></div>' +
        '<div class="erx-stories-viewer__bars">' + bars + '</div>' +
        '<button class="erx-stories-viewer__tap erx-stories-viewer__tap--prev" type="button" aria-label="Previous"></button>' +
        '<button class="erx-stories-viewer__tap erx-stories-viewer__tap--next" type="button" aria-label="Next"></button>' +
        '<div class="erx-stories-viewer__center" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="8,5 8,19 19,12"/></svg>' +
        '</div>' +
        '<button class="erx-stories-viewer__btn erx-stories-viewer__close" type="button" aria-label="Close">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>' +
        '</button>' +
        '<button class="erx-stories-viewer__btn erx-stories-viewer__mute" type="button" aria-label="Sound">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3z"/></svg>' +
        '</button>' +
      '</div>';

    document.body.appendChild(v);
    this.viewer = v;
    this.videoEl = v.querySelector('.erx-stories-viewer__video');
    this.barFills = Array.prototype.slice.call(v.querySelectorAll('.erx-stories-viewer__bar-fill'));
    this.muteBtn = v.querySelector('.erx-stories-viewer__mute');

    v.querySelector('.erx-stories-viewer__close').addEventListener('click', function () { self.close(); });
    v.addEventListener('click', function (e) { if (e.target === v) self.close(); });
    v.querySelector('.erx-stories-viewer__tap--prev').addEventListener('click', function () { if (self._suppressTap) return; self.prev(); });
    v.querySelector('.erx-stories-viewer__tap--next').addEventListener('click', function () { if (self._suppressTap) return; self.next(); });
    this.muteBtn.addEventListener('click', function () { self.toggleMute(); });

    this.videoEl.addEventListener('ended', function () { self.next(); });
    this.videoEl.addEventListener('click', function () { if (self._suppressTap) return; self.togglePlay(); });
    this.videoEl.addEventListener('play', function () { self.viewer.classList.remove('is-paused'); });
    this.videoEl.addEventListener('pause', function () {
      // Only surface the center play affordance for an intentional pause, not the
      // transient pause emitted while swapping src between stories or on end.
      if (self.userPaused && !self.videoEl.ended) self.viewer.classList.add('is-paused');
    });

    // Reels-style vertical + Instagram-style horizontal swipe navigation
    this.bindGestures();

    this.onKey = function (e) {
      if (self.viewer.hidden) return;
      if (e.key === 'Escape') self.close();
      else if (e.key === 'ArrowRight') self.next();
      else if (e.key === 'ArrowLeft') self.prev();
      else if (e.key === ' ') { e.preventDefault(); self.togglePlay(); }
    };
    document.addEventListener('keydown', this.onKey);

    this.updateMuteIcon();
  };

  /* Touch gestures: vertical (reels) up=next / down=prev, horizontal (instagram)
     left=next / right=prev, with live drag + slide transition. Mobile/touch only;
     desktop uses the tap zones too. Taps still hit the prev/next zones (suppressed only
     after a real drag, so swipe + tap never double-fire). */
  ErgonixStories.prototype.bindGestures = function () {
    var self = this;
    var stage = this.viewer.querySelector('.erx-stories-viewer__stage');
    if (!stage) return;
    this.stage = stage;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var sx = 0, sy = 0, st = 0, axis = null, dragging = false, active = false;
    function W() { return stage.clientWidth || 1; }
    function H() { return stage.clientHeight || 1; }
    function tf(axisX, val) { return axisX ? 'translate3d(' + val + '%,0,0)' : 'translate3d(0,' + val + '%,0)'; }
    function settle() {
      stage.style.transition = 'transform .2s cubic-bezier(.22,1,.36,1), opacity .2s';
      stage.style.transform = '';
      stage.style.opacity = '';
    }
    function navigate(action) {
      if (action === 'close') { settle(); self.close(); return; }
      var go = function () { if (action === 'next') self.next(); else self.prev(); };
      if (reduce) { stage.style.transition = 'none'; stage.style.transform = ''; stage.style.opacity = ''; go(); return; }
      var axisX = axis === 'x';
      var sign = self._lastSign;
      stage.style.transition = 'transform .16s ease, opacity .16s ease';
      stage.style.transform = tf(axisX, sign * 110);
      stage.style.opacity = '0.15';
      window.setTimeout(function () {
        go();
        if (!self.viewer || self.viewer.hidden) return; // next() closed at last story
        stage.style.transition = 'none';
        stage.style.transform = tf(axisX, -sign * 110);
        stage.style.opacity = '0.15';
        void stage.offsetWidth;
        stage.style.transition = 'transform .22s cubic-bezier(.22,1,.36,1), opacity .22s';
        stage.style.transform = '';
        stage.style.opacity = '';
      }, 160);
    }
    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      if (e.target.closest('.erx-stories-viewer__btn')) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; st = Date.now();
      axis = null; dragging = false; active = true;
      stage.style.transition = 'none';
    }, { passive: true });
    stage.addEventListener('touchmove', function (e) {
      if (!active) return;
      var dx = e.touches[0].clientX - sx, dy = e.touches[0].clientY - sy;
      if (!axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        dragging = true;
      }
      if (!dragging) return;
      e.preventDefault();
      if (axis === 'x') {
        var ox = dx * 0.55;
        stage.style.transform = 'translate3d(' + ox + 'px,0,0)';
        stage.style.opacity = String(Math.max(0.4, 1 - Math.abs(ox) / W()));
      } else {
        var oy = dy * 0.55;
        stage.style.transform = 'translate3d(0,' + oy + 'px,0)';
        stage.style.opacity = String(Math.max(0.4, 1 - Math.abs(oy) / H()));
      }
    }, { passive: false });
    stage.addEventListener('touchend', function (e) {
      if (!active) return;
      active = false;
      if (!dragging) return; // a tap -> let the tap-zone / play click run
      var c = (e.changedTouches && e.changedTouches[0]) || {};
      var dist = axis === 'x' ? (c.clientX - sx) : (c.clientY - sy);
      var vel = Math.abs(dist) / Math.max(1, Date.now() - st);
      self._suppressTap = true;
      window.setTimeout(function () { self._suppressTap = false; }, 380);
      var pass = Math.abs(dist) > (axis === 'x' ? W() * 0.18 : H() * 0.16) || vel > 0.45;
      if (!pass) { settle(); return; }
      self._lastSign = dist < 0 ? -1 : 1;
      if (axis === 'x') {
        navigate(dist < 0 ? 'next' : 'prev');
      } else if (dist < 0) {
        navigate('next');
      } else {
        navigate(self.index === 0 ? 'close' : 'prev');
      }
    }, { passive: true });
    stage.addEventListener('touchcancel', function () { active = false; if (dragging) settle(); }, { passive: true });
  };

  ErgonixStories.prototype.open = function (index) {
    this.buildViewer();
    window.clearTimeout(this.closeTimer);
    this.lastFocus = document.activeElement;
    this.index = index;
    this.viewer.hidden = false;
    document.body.classList.add('erx-stories-lock');
    if (this.stage) { this.stage.style.transition = 'none'; this.stage.style.transform = ''; this.stage.style.opacity = ''; }
    // force reflow so the opacity transition runs
    void this.viewer.offsetWidth;
    this.viewer.classList.add('is-open');
    // Move focus into the dialog so Esc/Tab stay scoped to the viewer.
    var closeBtn = this.viewer.querySelector('.erx-stories-viewer__close');
    if (closeBtn && closeBtn.focus) closeBtn.focus();
    this.play();
  };

  ErgonixStories.prototype.play = function () {
    var story = this.stories[this.index];
    if (!story) { this.close(); return; }

    this.setBars();
    this.userPaused = false;
    this.viewer.classList.remove('is-paused');
    this.videoEl.muted = this.muted;
    if (story.poster) this.videoEl.setAttribute('poster', story.poster);
    else this.videoEl.removeAttribute('poster');
    this.videoEl.src = story.video;
    this.videoEl.currentTime = 0;

    var self = this;
    var p = this.videoEl.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        // Autoplay with sound blocked -> fall back to muted.
        self.muted = true;
        self.videoEl.muted = true;
        self.updateMuteIcon();
        var p2 = self.videoEl.play();
        if (p2 && typeof p2.catch === 'function') p2.catch(function () {});
      });
    }
    this.startProgress();
  };

  ErgonixStories.prototype.startProgress = function () {
    var self = this;
    cancelAnimationFrame(this.raf);
    var tick = function () {
      var d = self.videoEl.duration;
      var fill = self.barFills[self.index];
      if (fill && d && isFinite(d)) {
        fill.style.width = Math.min(100, (self.videoEl.currentTime / d) * 100) + '%';
      }
      self.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  };

  ErgonixStories.prototype.setBars = function () {
    for (var i = 0; i < this.barFills.length; i++) {
      this.barFills[i].style.width = i < this.index ? '100%' : '0%';
    }
  };

  ErgonixStories.prototype.next = function () {
    if (this.index >= this.stories.length - 1) { this.close(); return; }
    this.index++;
    this.play();
  };

  ErgonixStories.prototype.prev = function () {
    if (this.index <= 0) { this.videoEl.currentTime = 0; return; }
    this.index--;
    this.play();
  };

  ErgonixStories.prototype.togglePlay = function () {
    if (this.videoEl.paused) {
      this.userPaused = false;
      this.videoEl.play();
    } else {
      this.userPaused = true;
      this.videoEl.pause();
    }
  };

  ErgonixStories.prototype.toggleMute = function () {
    this.muted = !this.muted;
    this.videoEl.muted = this.muted;
    this.updateMuteIcon();
  };

  ErgonixStories.prototype.updateMuteIcon = function () {
    var on = '<path d="M3 9v6h4l5 5V4L7 9H3z"/><path d="M16 8.8a4 4 0 0 1 0 6.4l1.2 1.2a5.7 5.7 0 0 0 0-8.8L16 8.8z"/>';
    var off = '<path d="M3 9v6h4l5 5V4L7 9H3z"/><path d="m17 7 4 4m0-4-4 4" stroke="#fff" stroke-width="2" fill="none"/>';
    this.muteBtn.querySelector('svg').innerHTML = this.muted ? off : on;
    this.muteBtn.setAttribute('aria-pressed', this.muted ? 'true' : 'false');
    this.muteBtn.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
  };

  ErgonixStories.prototype.close = function () {
    if (!this.viewer || this.viewer.hidden) return;
    cancelAnimationFrame(this.raf);
    this.viewer.classList.remove('is-open');
    this.videoEl.pause();
    this.videoEl.removeAttribute('src');
    this.videoEl.load();
    document.body.classList.remove('erx-stories-lock');
    var self = this;
    this.closeTimer = window.setTimeout(function () { self.viewer.hidden = true; }, 200);
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-ergonix-stories]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.erxStoriesReady) return;
      root.dataset.erxStoriesReady = '1';
      new ErgonixStories(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-init when a section is added/re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
